// Serialization helpers for runtime import graphs and mode maps.
// Worker compile uses these plain-object payloads to rebuild sync resolvers.
import type { LuisCompileMode, LuisImportResolver, LuisResolvedImport } from '@lentystyle/compiler'
import type { ImportGraph, ImportModeResolver, SerializedImportGraph, SerializedImportModeMap } from './types'

// Convert runtime import graph maps into structured-clone-safe plain objects.
export function serializeImportGraph(graph: ImportGraph): SerializedImportGraph {
  const serialized: SerializedImportGraph = {}
  // Copy each source edge-map into plain nested records.
  for (const [sourceId, requests] of graph) {
    serialized[sourceId] = {}
    // Copy each raw/trimmed request edge into current source record.
    for (const [request, resolved] of requests) {
      serialized[sourceId][request] = { ...resolved }
    }
  }

  // Return plain-object import graph payload.
  return serialized
}

// Convert compile mode lookup map into a worker-safe plain record.
export function serializeImportModeMap(sourceModes: Map<string, LuisCompileMode>): SerializedImportModeMap {
  const serialized: SerializedImportModeMap = {}
  // Copy every source compile mode into plain record form.
  for (const [sourceId, mode] of sourceModes) {
    serialized[sourceId] = mode
  }

  // Return plain-object source mode payload.
  return serialized
}

// Rebuild sync import resolver from structured-clone-safe graph payload.
export function createSerializedImportResolver(graph: SerializedImportGraph): LuisImportResolver | undefined {
  const sourceIds = Object.keys(graph)
  // Return undefined when source has no serialized `.luis` imports.
  if (sourceIds.length === 0) {
    return undefined
  } else {
    // Continue resolver creation for populated graph payload.
  }

  // Return sync lookup resolver over plain-object import edges.
  return (request: string, fromSourceId: string | null) => {
    const fromId = fromSourceId ?? sourceIds[0]
    const bySource = graph[fromId] ?? graph[sourceIds[0]]
    // Return null when source has no recorded import edges.
    if (!bySource) {
      return null
    } else {
      // Continue exact and trimmed request lookup.
    }

    const direct = bySource[request]
    if (direct) {
      return cloneResolvedImport(direct)
    } else {
      // Continue trimmed-key fallback lookup.
    }

    const trimmed = bySource[request.trim()]
    // Return trimmed import payload when available.
    return trimmed ? cloneResolvedImport(trimmed) : null
  }
}

// Rebuild compile mode resolver from structured-clone-safe mode payload.
export function createSerializedImportModeResolver(sourceModes: SerializedImportModeMap): ImportModeResolver | undefined {
  const sourceIds = Object.keys(sourceModes)
  // Return undefined when mode payload is empty.
  if (sourceIds.length === 0) {
    return undefined
  } else {
    // Continue source-mode resolver creation.
  }

  // Return compile mode lookup over plain source-id record.
  return (sourceId: string): LuisCompileMode | undefined => sourceModes[sourceId]
}

// Clone resolved import payload to avoid accidental shared mutations.
function cloneResolvedImport(resolved: LuisResolvedImport): LuisResolvedImport {
  // Return shallow-cloned import payload for compile resolver safety.
  return { ...resolved }
}
